import { useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Search, Inbox, MoreHorizontal, Settings, User as UserIcon } from 'lucide-react';
import { useAuth } from '@/hooks/auth/useAuth';
import { ConnectionIndicator } from '@/components/status/ConnectionIndicator';
import { useInboxCount } from '@/hooks/ui/useInboxCount';
import { Button } from '@reflex/ui/components/ui/button';
import { cn } from '@reflex/ui/lib/utils';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@reflex/ui/components/ui/sheet';
import { ROUTES } from '@/lib/routes';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import type { MobileBottomNavProps, NavItem } from './nav-types';
import { useNavSources } from './useNavSources';
import { OrgSwitcher } from './OrgSwitcher';
import { useUserMenuItems } from './account-nav';
import { useConfigureEntry } from '../ConfigureShell';
import { useTakeoverFooterItems } from '../TakeoverShell/useTakeoverFooterItems';

interface PrimaryTabProps {
  item: NavItem;
  pathname: string;
  runsRoute: string;
  agentsPrefix: string;
  badge?: number;
  onClick?: () => void;
}

function PrimaryTab({ item, pathname, runsRoute, agentsPrefix, badge, onClick }: PrimaryTabProps) {
  const { to, label, icon: Icon } = item;
  const exactMatch = pathname === to;
  const active =
    exactMatch ||
    (to !== '/' && pathname.startsWith(to + '/')) ||
    (to === runsRoute && pathname.startsWith(agentsPrefix));
  return (
    <NavLink
      to={to}
      end={to === '/'}
      data-testid={`nav-item-${to}`}
      data-active={active ? 'true' : undefined}
      onClick={onClick}
      className={({ isActive }) => {
        const anyActive = isActive || active;
        return `flex flex-1 flex-col items-center justify-center gap-0.5 px-1 text-3xs transition-colors ${
          anyActive ? 'text-sidebar-accent-foreground' : 'text-muted-foreground'
        }`;
      }}
      aria-label={label}
    >
      <div className="relative">
        <Icon className="h-5 w-5" />
        {badge !== undefined && badge > 0 && (
          <span className="absolute -right-1.5 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-status-warning px-0.5 text-[8px] font-bold text-white">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </div>
      <span>{label}</span>
    </NavLink>
  );
}

const sheetRowClass =
  'flex w-full items-center gap-3 rounded-md px-3 py-3 text-left text-sm text-foreground transition-colors hover:bg-sidebar-accent';

function UserAvatar({ login, avatarUrl }: { login: string; avatarUrl?: string | null }) {
  const [imgError, setImgError] = useState(false);
  if (avatarUrl && !imgError) {
    return (
      <img
        src={avatarUrl}
        alt={`${login}'s avatar`}
        className="h-9 w-9 shrink-0 rounded-full object-cover ring-1 ring-border/40"
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted/60 text-sm font-medium text-muted-foreground ring-1 ring-border/40">
      {login.charAt(0).toUpperCase()}
    </div>
  );
}

export function MobileBottomNav({ wsState, onCommandOpen, hidden = false }: MobileBottomNavProps) {
  const { authEnabled, logout, user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const inboxCount = useInboxCount();
  const [moreOpen, setMoreOpen] = useState(false);
  const orgRoutes = useOrgRoutes();
  const {
    allMainNavItems,
    visibleDrillGroups,
    labsItem,
    takeoverBottomItems,
    pluginBottomItems,
    scopedPluginNavItems,
  } = useNavSources();
  const configureEntry = useConfigureEntry();
  const userMenuItems = useUserMenuItems();
  // The takeover aside's pinned footer is desktop-only, so its items (Send
  // feedback, Documentation, Releases) reach mobile through this sheet.
  // Send feedback is contributed to both groups, so dedupe by target.
  const takeoverFooterItems = useTakeoverFooterItems();
  const accountSheetItems = useMemo<NavItem[]>(() => {
    const seen = new Set(userMenuItems.map((item) => item.to));
    return [...userMenuItems, ...takeoverFooterItems.filter((item) => !seen.has(item.to))];
  }, [userMenuItems, takeoverFooterItems]);

  const connectItem = useMemo(
    () => takeoverBottomItems.find((item) => item.tutorialId === 'nav-connect'),
    [takeoverBottomItems],
  );

  // Curated bottom-bar slots. This is intentionally NOT derived from the main
  // nav — the bottom bar is a hand-picked tab set (the documented exception
  // to the otherwise auto-synced mobile/desktop nav). Labs, Connect, and
  // Configure live in the More sheet as takeover doors.
  const primaryRoutes = useMemo<readonly string[]>(
    () => [orgRoutes.inbox, orgRoutes.runs, orgRoutes.automations] as const,
    [orgRoutes.inbox, orgRoutes.runs, orgRoutes.automations],
  );

  // Inbox is a built-in core route that no nav-item builder contributes, so we
  // provide a fallback for it. Sessions / Automations are plugin-provided —
  // if their plugin isn't installed we drop the tab instead of rendering a
  // dead link to a 404.
  const primaryFallbacks = useMemo<Record<string, NavItem>>(
    () => ({
      [orgRoutes.inbox]: { to: orgRoutes.inbox, label: 'Inbox', icon: Inbox },
    }),
    [orgRoutes.inbox],
  );

  const homeRoute = orgRoutes.home || ROUTES.root;
  const agentsPrefix = `${orgRoutes.home}/agents/`;

  const primaryItems = useMemo<NavItem[]>(() => {
    // Search across every nav contribution — main items, drill group children,
    // and raw plugin items — so a route contributed in any of them lights up
    // its bottom-bar slot. Slots with no contribution and no fallback are
    // dropped to avoid linking to a 404.
    const universe: NavItem[] = [
      ...allMainNavItems,
      ...visibleDrillGroups.flatMap((g) => g.items),
      ...scopedPluginNavItems,
    ];
    return primaryRoutes
      .map((route) => universe.find((i) => i.to === route) ?? primaryFallbacks[route])
      .filter((item): item is NavItem => item !== undefined);
  }, [primaryRoutes, allMainNavItems, visibleDrillGroups, scopedPluginNavItems, primaryFallbacks]);

  const primaryRouteSet = useMemo(() => new Set<string>(primaryRoutes), [primaryRoutes]);

  // Takeover doors first (Labs, Connect, Configure), then leftover main-nav
  // destinations. Home is the org root the Sessions tab already covers.
  const mainSheetItems = useMemo<NavItem[]>(() => {
    const doors: NavItem[] = [labsItem, ...(connectItem ? [connectItem] : []), configureEntry];
    const doorTos = new Set(doors.map((item) => item.to));
    const rest = allMainNavItems.filter(
      (i) => !primaryRouteSet.has(i.to) && i.to !== homeRoute && !doorTos.has(i.to),
    );
    return [...doors, ...rest];
  }, [allMainNavItems, primaryRouteSet, homeRoute, labsItem, connectItem, configureEntry]);

  function handleNavigate(to: string) {
    setMoreOpen(false);
    if (to.startsWith('#')) {
      window.dispatchEvent(new CustomEvent('plugin:command', { detail: to }));
      return;
    }
    navigate(to);
  }

  function handleSheetOpenChange(open: boolean) {
    setMoreOpen(open);
  }

  return (
    <>
      <nav
        aria-label="Mobile navigation"
        aria-hidden={hidden || undefined}
        // The explicit height is a contract with `AppShell` (Layout.tsx),
        // whose mobile <main> reserves `3.5rem + safe-area-inset-bottom` of
        // bottom padding. Content-driven tab height drifts with font metrics
        // and ends up overlapping the content above the bar.
        className={cn(
          'fixed inset-x-0 bottom-0 z-40 flex h-[calc(3.5rem+env(safe-area-inset-bottom))] items-stretch border-t border-border bg-sidebar pb-[env(safe-area-inset-bottom)] transition-transform duration-200 md:hidden',
          hidden && 'pointer-events-none translate-y-full',
        )}
      >
        {primaryItems.map((item) => (
          <PrimaryTab
            key={item.to}
            item={item}
            pathname={pathname}
            runsRoute={orgRoutes.runs}
            agentsPrefix={agentsPrefix}
            badge={item.to === orgRoutes.inbox ? inboxCount : undefined}
          />
        ))}
        <Button
          variant="ghost"
          onClick={() => setMoreOpen(true)}
          className="flex h-auto flex-1 flex-col items-center justify-center gap-0.5 rounded-none px-1 py-0 text-3xs text-muted-foreground"
          aria-label="More"
        >
          <MoreHorizontal className="h-5 w-5" />
          <span>More</span>
        </Button>
      </nav>

      <Sheet open={moreOpen} onOpenChange={handleSheetOpenChange}>
        <SheetContent
          side="bottom"
          className="max-h-[85vh] rounded-t-xl pb-[env(safe-area-inset-bottom)]"
        >
          <RootSheetContent
            user={user}
            authEnabled={authEnabled}
            mainItems={mainSheetItems}
            pluginBottomItems={pluginBottomItems}
            userMenuItems={accountSheetItems}
            wsState={wsState}
            onNavigate={handleNavigate}
            onSearch={() => {
              setMoreOpen(false);
              onCommandOpen();
            }}
            onLogout={() => {
              setMoreOpen(false);
              void logout();
            }}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}

interface RootSheetContentProps {
  user: ReturnType<typeof useAuth>['user'];
  authEnabled: boolean;
  mainItems: NavItem[];
  pluginBottomItems: NavItem[];
  userMenuItems: NavItem[];
  wsState: MobileBottomNavProps['wsState'];
  onNavigate: (to: string) => void;
  onSearch: () => void;
  onLogout: () => void;
}

function SheetItem({ item, onNavigate }: { item: NavItem; onNavigate: (to: string) => void }) {
  const { to, label, icon: Icon, openInNewWindow } = item;
  if (openInNewWindow) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noreferrer"
        data-testid={`nav-item-${to}`}
        className={sheetRowClass}
        aria-label={label}
      >
        <Icon className="h-5 w-5 text-muted-foreground" />
        <span>{label}</span>
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onNavigate(to)}
      data-testid={`nav-item-${to}`}
      className={sheetRowClass}
      aria-label={label}
    >
      <Icon className="h-5 w-5 text-muted-foreground" />
      <span>{label}</span>
    </button>
  );
}

function RootSheetContent({
  user,
  authEnabled,
  mainItems,
  pluginBottomItems,
  userMenuItems,
  wsState,
  onNavigate,
  onSearch,
  onLogout,
}: RootSheetContentProps) {
  return (
    <>
      <SheetHeader className="pb-0">
        <SheetTitle>More</SheetTitle>
        <SheetDescription className="sr-only">
          Account, navigation, and additional actions
        </SheetDescription>
      </SheetHeader>
      <div className="overflow-y-auto px-2 pb-4">
        {authEnabled && (
          <div
            className="mb-2 flex flex-col gap-1.5 rounded-lg border border-border/40 bg-muted/30 p-2"
            data-testid="mobile-account-header"
          >
            <OrgSwitcher />
            {user && (
              <div className="flex items-center gap-2.5 px-2.5 py-1.5">
                <UserAvatar login={user.login} avatarUrl={user.avatarUrl} />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium text-foreground">{user.login}</span>
                  {user.email && (
                    <span className="truncate text-2xs text-muted-foreground">{user.email}</span>
                  )}
                </div>
              </div>
            )}
            <div className="flex gap-1 px-1">
              <button
                type="button"
                onClick={() => onNavigate(ROUTES.profile)}
                data-testid={`nav-item-${ROUTES.profile}`}
                className={`${sheetRowClass} flex-1 py-2 text-xs`}
                aria-label="Profile"
              >
                <UserIcon className="h-4 w-4 text-muted-foreground" />
                <span>Profile</span>
              </button>
              <button
                type="button"
                onClick={() => onNavigate(ROUTES.settings)}
                data-testid={`nav-item-${ROUTES.settings}`}
                className={`${sheetRowClass} flex-1 py-2 text-xs`}
                aria-label="Settings"
              >
                <Settings className="h-4 w-4 text-muted-foreground" />
                <span>Settings</span>
              </button>
            </div>
          </div>
        )}

        {mainItems.map((item) => (
          <SheetItem key={item.to} item={item} onNavigate={onNavigate} />
        ))}
        {pluginBottomItems.map((item) => (
          <SheetItem key={item.to} item={item} onNavigate={onNavigate} />
        ))}
        {userMenuItems.map((item) => (
          <SheetItem key={item.to} item={item} onNavigate={onNavigate} />
        ))}

        <div className="my-2 border-t border-border" />
        <Button
          variant="ghost"
          onClick={onSearch}
          className="flex h-auto w-full items-center justify-start gap-3 rounded-md px-3 py-3 text-sm font-normal text-foreground"
          aria-label="Search"
        >
          <Search className="h-5 w-5 text-muted-foreground" />
          <span>Search</span>
        </Button>
        {authEnabled && (
          <Button
            variant="ghost"
            onClick={onLogout}
            className="flex h-auto w-full items-center justify-start gap-3 rounded-md px-3 py-3 text-sm font-normal text-foreground"
            aria-label="Sign out"
          >
            <LogOut className="h-5 w-5 text-muted-foreground" />
            <span>Sign out</span>
          </Button>
        )}
        <div className="mt-2 flex items-center gap-3 px-3 py-2 text-xs text-muted-foreground">
          <ConnectionIndicator state={wsState} iconOnly />
          <span>Connection</span>
        </div>
      </div>
    </>
  );
}
